/**
 * Repeated rows and admin toggles: what the user switched off.
 *
 * ROWS come from Figma (`<key>#group`, see Scene.rows). Hiding one removes its
 * elements and nothing else: every other element stays exactly where the
 * designer put it.
 *
 * TOGGLES come from the admin (Template.toggles): a layer the user may switch
 * off. A layer marked inside a row stands for the same layer in EVERY row of
 * that stack, so the user gets one switch per row.
 *
 * Everything here is pure and identity-stable — with nothing to hide,
 * visibleScene hands back the SAME scene object, because SceneRenderer resets
 * its image-load accounting whenever it gets a new one.
 */
import {Scene, SceneElement} from './types';

export interface ToggleDef {
    label: string;
    defaultOn: boolean;
}

/** Admin config, keyed by the Figma node id of the marked layer. */
export type ToggleDefs = Record<string, ToggleDef>;

/** One switch the user sees. `key` is what Visibility stores. */
export interface Toggle {
    key: string;
    label: string;
    /** The row this switch belongs to; absent for a template-wide switch. */
    row?: string;
    defaultOn: boolean;
}

/** The user's choices, as OVERRIDES: a row is shown and a toggle is at its
 *  default unless listed here, so a row or toggle added by a later Figma
 *  re-sync never inherits a stale "off". */
export interface Visibility {
    hiddenRows: string[];
    toggles: Record<string, boolean>;
}

export const DEFAULT_VISIBILITY: Visibility = {hiddenRows: [], toggles: {}};

export const toggleKey = (label: string, row?: string): string => (row ? `${row}::${label}` : label);

// Own properties only: a switch labelled "constructor" must not read Object.prototype.
export const isToggleOn = (toggle: Toggle, visibility: Visibility): boolean =>
    Object.prototype.hasOwnProperty.call(visibility.toggles, toggle.key) ? visibility.toggles[toggle.key] === true : toggle.defaultOn;

/**
 * A row layer's counterparts across its stack, as [row, nodeId] pairs.
 * Duplicated rows share a structure, so the n-th layer called "icon" in one row
 * is the n-th layer called "icon" in each of the others. Counting by name, not
 * by position, survives a designer adding a layer to a single row.
 */
const rowTwins = (scene: Scene): ((nodeId: string) => [string, string][]) => {
    const signatureOf = new Map<string, { row: string; signature: string }>();
    const nodeAt = new Map<string, string>();
    const seen = new Map<string, number>();
    for (const el of scene.elements) {
        // Overlay rects share their image's node id; the first element speaks for the node.
        if (!el.groupId || !el.sourceNodeId || signatureOf.has(el.sourceNodeId)) continue;
        const nth = seen.get(`${el.groupId}\u0000${el.name}`) ?? 0;
        seen.set(`${el.groupId}\u0000${el.name}`, nth + 1);
        const signature = `${el.name}\u0000${nth}`;
        signatureOf.set(el.sourceNodeId, {row: el.groupId, signature});
        nodeAt.set(`${el.groupId}\u0000${signature}`, el.sourceNodeId);
    }
    const rows = scene.rows ?? [];
    return (nodeId) => {
        const me = signatureOf.get(nodeId);
        const stack = rows.find((row) => row.key === me?.row)?.stack;
        if (!me || stack === undefined) return me ? [[me.row, nodeId]] : [];
        return rows
            .filter((row) => row.stack === stack)
            .flatMap((row): [string, string][] => {
                const twin = nodeAt.get(`${row.key}\u0000${me.signature}`);
                return twin ? [[row.key, twin]] : [];
            });
    };
};

/** Which layers of `scene` carry a switch, by Figma node id. Layers sharing a
 *  label share ONE switch, so the first of them decides its default — otherwise
 *  the switch could read "on" while one of its layers renders off. */
export const resolveToggles = (scene: Scene, defs: ToggleDefs = {}): Map<string, Toggle> => {
    const out = new Map<string, Toggle>();
    if (!Object.keys(defs).length) return out;
    const twins = rowTwins(scene);
    const byKey = new Map<string, Toggle>();
    for (const el of scene.elements) {
        const def = el.sourceNodeId ? defs[el.sourceNodeId] : undefined;
        if (!def || out.has(el.sourceNodeId!)) continue;
        const mark = (nodeId: string, row?: string) => {
            if (out.has(nodeId)) return;
            const key = toggleKey(def.label, row);
            if (!byKey.has(key)) byKey.set(key, {key, label: def.label, row, defaultOn: def.defaultOn});
            out.set(nodeId, byKey.get(key)!);
        };
        if (el.groupId) twins(el.sourceNodeId!).forEach(([row, nodeId]) => mark(nodeId, row));
        else mark(el.sourceNodeId!);
    }
    return out;
};

/** The switches of one scene, one per key, in layer order. */
export const togglesOf = (scene: Scene, defs?: ToggleDefs): Toggle[] => {
    const toggles = resolveToggles(scene, defs);
    const byKey = new Map<string, Toggle>();
    for (const el of scene.elements) {
        const toggle = el.sourceNodeId ? toggles.get(el.sourceNodeId) : undefined;
        if (toggle && !byKey.has(toggle.key)) byKey.set(toggle.key, toggle);
    }
    return [...byKey.values()];
};

/** The scene as the user sees it: hidden rows and switched-off layers removed. */
export const visibleScene = (scene: Scene, defs?: ToggleDefs, visibility: Visibility = DEFAULT_VISIBILITY): Scene => {
    const toggles = resolveToggles(scene, defs);
    const hidden = new Set(visibility.hiddenRows);
    const gone = (el: SceneElement): boolean => {
        if (el.groupId !== undefined && hidden.has(el.groupId)) return true;
        const toggle = el.sourceNodeId ? toggles.get(el.sourceNodeId) : undefined;
        return !!toggle && !isToggleOn(toggle, visibility);
    };
    return scene.elements.some(gone) ? {...scene, elements: scene.elements.filter((el) => !gone(el))} : scene;
};

/** Input keys the user no longer sees: painted in `raw`, gone from `visible`. */
export const hiddenInputKeys = (raw: Scene, visible: Scene): Set<string> => {
    if (raw === visible) return new Set();
    const shown = new Set(visible.elements.map((el) => el.inputKey));
    return new Set(raw.elements.flatMap((el) => (el.inputKey && !shown.has(el.inputKey) ? [el.inputKey] : [])));
};

/** Input key → the row it belongs to, across every design. */
export const inputRows = (scenes: Scene[]): Map<string, string> => {
    const out = new Map<string, string>();
    for (const scene of scenes) {
        for (const el of scene.elements) {
            if (el.inputKey && el.groupId !== undefined && !out.has(el.inputKey)) out.set(el.inputKey, el.groupId);
        }
    }
    return out;
};

/** A draft's stored visibility, reduced to rows and switches these scenes still have. */
export const restoreVisibility = (raw: unknown, scenes: Scene[], defs?: ToggleDefs): Visibility => {
    const stored = (raw && typeof raw === 'object' ? raw : {}) as { hiddenRows?: unknown; toggles?: unknown };
    const rowKeys = new Set(scenes.flatMap((scene) => (scene.rows ?? []).map((row) => row.key)));
    const toggleKeys = new Set(scenes.flatMap((scene) => togglesOf(scene, defs).map((toggle) => toggle.key)));
    const hiddenRows = Array.isArray(stored.hiddenRows)
        ? [...new Set(stored.hiddenRows.filter((key): key is string => typeof key === 'string' && rowKeys.has(key)))]
        : [];
    const toggles = Object.fromEntries(
        Object.entries(stored.toggles && typeof stored.toggles === 'object' ? stored.toggles : {})
            .filter(([key, on]) => toggleKeys.has(key) && typeof on === 'boolean'),
    ) as Record<string, boolean>;
    return {hiddenRows, toggles};
};
